import { v4 as uuidv4 } from 'uuid';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { JsmIncidentManagementSetupError } from './run-jsm-incident-management-setup';

import type { JsmIncidentManagementSetupState } from './jsm-incident-management-setup-state';

export const createJsmIncidentSetupAnalytics = (createAnalyticsEvent: CreateUIAnalyticsEvent) => {
	const attemptId = uuidv4();
	const startedAt = Date.now();
	let stageStartedAt = startedAt;
	let current: JsmIncidentManagementSetupState | undefined;
	let settled = false;
	let admitted = false;
	const track = (
		action: 'started' | 'succeeded' | 'failed' | 'cancelled',
		stage: string,
		failure?: JsmIncidentManagementSetupError,
	) => {
		const { apiError, reason } = failure ?? {};
		const {
			diagnostics = {},
			asyncRequestId,
			kind: errorKind,
			operation,
			mutationOutcome,
		} = apiError ?? {};
		const { requestId, httpStatus, traceId } = diagnostics;
		createAnalyticsEvent({
			eventType: 'operational',
			actionSubject: 'jsmIncidentSetup',
			action,
			attributes: {
				attemptId,
				stage,
				elapsedTimeMs: Date.now() - startedAt,
				stageElapsedTimeMs: Date.now() - (stage === 'setup' ? startedAt : stageStartedAt),
				asyncRequestId: asyncRequestId ?? current?.asyncRequestId,
				requestId,
				reason,
				errorKind,
				operation,
				mutationOutcome,
				httpStatus,
				traceId,
			},
		}).fire();
	};
	return {
		start: () => {
			if (admitted || settled) return;
			admitted = true;
			track('started', 'setup');
		},
		progress: (state: JsmIncidentManagementSetupState) => {
			if (settled || current?.stage === state.stage) return;
			if (current) track('succeeded', current.stage);
			current = state;
			stageStartedAt = Date.now();
			if (state.stage === 'complete') {
				settled = true;
				track('succeeded', 'setup');
			} else {
				track('started', state.stage);
			}
		},
		fail: (failure: JsmIncidentManagementSetupError) => {
			if (settled) return;
			settled = true;
			track('failed', current?.stage ?? (admitted ? 'setup' : 'validation'), failure);
		},
		cancel: () => {
			if (!admitted || settled) return;
			settled = true;
			track(
				'cancelled',
				current?.stage ?? 'setup',
				new JsmIncidentManagementSetupError('interrupted'),
			);
		},
	};
};
